import ua from '@tencent/docs-user-agent';
import { getQuery } from 'sutils';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '../../slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { initWemeetAgentConfig } from './agentConfig';
import isEqual from 'lodash/isEqual';
import type { WebDocRecordRectConfig } from '@tencent/wemeet-js-sdk/build/types/appshare';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { AppEntryType } from '@src/slide/infrastructure/entry/base/infrastructure-base.interface';

export const isSpeaker = (): boolean => getQuery('login') === 'speaker';

async function loadTencentMeetingSDK() {
    const tencentMeetingSDK = await import(/* webpackChunkName: "tencent-meeting" */ '@tencent/wemeet-js-sdk');
    await initWemeetAgentConfig();
    return tencentMeetingSDK;
}

type WeMeetSdk = typeof import('@tencent/wemeet-js-sdk');
let loadingPromise: Promise<WeMeetSdk>;
export async function getTencentMeetingSDK(): Promise<WeMeetSdk> {
    if (loadingPromise) return loadingPromise;

    loadingPromise = loadTencentMeetingSDK();
    return loadingPromise;
}

// 腾讯会议下主动关闭客户端的加载loading
export const closeLoadingInTencentMeeting = async (): Promise<void> => {
    if (!ua.isTencentMeeting) return;

    if (window.tdocs?.closeLoading) {
        window.tdocs.closeLoading();
        return;
    }

    const { ui } = await getTencentMeetingSDK();
    ui?.closeLoading?.();
};

export async function initInTencentMeeting(): Promise<void> {
    if (!ua.isTencentMeeting) return;

    const { appShare } = await getTencentMeetingSDK();
    appShare.startLoadDocPage({
        docType: 4,
        docUrl: location.href,
    });

    // 非演示, 通知会议录制全屏
    if (AppServiceProvider.getInstance().getAppServices()
        .getEntryType() !== AppEntryType.MEETING_ROOM) {
        appShare.setRecordRect({
            x: 0,
            y: 0,
            width: 0,
            height: 0,
        });
    }
}

/** 获取会议 id, 可能为空 */
export async function getMeetingID(): Promise<string | undefined> {
    if (!ua.isTencentMeeting) return;

    try {
        const { data: dataUtil } = await getTencentMeetingSDK();
        const meetingInfo = await dataUtil.getCurrMeetingInfo();
        return meetingInfo.meetingId;
    } catch (error) {
        window.log.error(`获取会议信息失败 ${JSON.stringify(error)}`);
    }
}

// 会议上报 https://docs.qq.com/sheet/BYeyFa0J8NYX4wVI1f2dm7HZ18QPLK0Rx8si1?tab=knagjw
export const reportForMeeting = (): void => {
    const meetFrom = getQuery('meet_from');
    switch (meetFrom) {
        case 'new':
        case 'insert':
        case 'select':
            tdw({
                opername: TDWOperationType.DOC_MEET_WEB,
                module: TDWModuleType.SHARE_IN_MEET,
                action: TDWActionType.BEGIN_PRESENTATION,
                ver10: meetFrom,
            });
            break;
        case 'web_select':
            tdw({
                opername: TDWOperationType.DOC_MEET_WEB,
                module: TDWModuleType.DOC_TO_MEET,
                action: TDWActionType.BEGIN_PRESENTATION_SET,
            });
            break;
    }
};

/**
 * 移动端调用腾讯会议 sdk 告知会议，文档是否可编辑（在可编辑态文档内会隐藏表情和评论栏）
 * @param canEdit 是否可编辑
 */
export async function setDocCanEditInTencentMeeting(canEdit: boolean): Promise<void> {
    if (!ua.isTencentMeeting) return;

    try {
        const { appShare } = await getTencentMeetingSDK();
        await appShare?.setDocEditState?.({ state: canEdit });
    } catch (error) {
        window.log.error(`通知会议目前是否在编辑态失败 ${JSON.stringify(error)}`);
    }
}

/**
 * 移动端调用腾讯会议 sdk 告知会议，本品类文档的最佳文档流展示方向是什么，PPT是横屏
 */
export async function setDocViewDirectionInTencentMeeting(): Promise<void> {
    if (!ua.isTencentMeeting) return;

    try {
        const { appShare, DocViewDirectionType } = await getTencentMeetingSDK();
        await appShare?.setDocViewDirection?.({ direction: DocViewDirectionType.LANDSCAPE });
    } catch (error) {
        window.log.error(`通知会议文档最佳展示方向失败 ${JSON.stringify(error)}`);
    }
}

let lastRect: WebDocRecordRectConfig | null = null;
/** 设置会议录制区域 */
export async function updatePresentationRect(domRect: DOMRect): Promise<void> {
    if (!ua.isTencentMeeting) return;

    let rect: WebDocRecordRectConfig | null = null;
    try {
        const { left, top, width, height } = domRect;
        if (width === 0 || height === 0) return;

        rect = {
            x: Math.max(left, 0),
            y: Math.max(top, 0),
            width,
            height,
        };
        if (isEqual(rect, lastRect)) return;

        if (__IS_DEV__) drawRecordRectForDev(rect);

        window.log.info(`[TencentMeeting] setRecordRect ${rect.x} ${rect.y} ${rect.width} ${rect.height}`);
        const { appShare } = await getTencentMeetingSDK();
        await appShare.setRecordRect(rect);
        lastRect = rect;
    } catch (error) {
        window.log.error(`[TencentMeeting] setRecordRect failed ${JSON.stringify(rect)} ${JSON.stringify(error)}`);
    }
}

let recordRectDiv: HTMLDivElement | null;
/** [devOnly] 绘制录制区域*/
function drawRecordRectForDev(rect: WebDocRecordRectConfig) {
    if (!recordRectDiv) {
        recordRectDiv = document.createElement('div');
        recordRectDiv.style.border = '10px solid green';
        recordRectDiv.style.position = 'fixed';
        document.body.appendChild(recordRectDiv);
    }

    recordRectDiv.style.width = `${rect.width}px`;
    recordRectDiv.style.height = `${rect.height}px`;
    recordRectDiv.style.top = `${rect.y}px`;
    recordRectDiv.style.left = `${rect.x}px`;
}

/**
 * 会议内, 暂停录制
 *
 * NOTE: 会议接口设计问题, 接口虽是 enableRecordRect({ enable: true }), 实际功能是终止
 */
export async function stopRecordRect(): Promise<void> {
    if (!ua.isTencentMeeting) return;

    try {
        const { appShare } = await getTencentMeetingSDK();
        await appShare?.enableRecordRect({ enable: true });
    } catch (error) {
        window.log.error('stopRecordRect failed');
    }
}
